/**
 * The words and figures of the plan screens (plan-020 **WAPP-PLAN**; design §32.12.3, §32.17) — pure, exported for the
 * suites. Every figure is the API's: an offer's fee, included seconds, calls at once and rates come from the offers
 * read, a quote's amounts from its frozen detail, a running plan's from the subscription read. Nothing here types a
 * price (`no-literal-plan-figure`, `no-literal-talk-price`).
 *
 * Per-second billing (OA-1): talk time is shown in minutes AND seconds ("125 min 30 s"), never rounded to the minute;
 * an included-minutes count that is not whole keeps its fraction ("120.5 minutes").
 *
 * Offer names and taglines are data (design §32.12.1 rule 6): {@link localisedOf} picks the page language's line.
 */
import { ApiError } from "@wetalk/api-client";
import { money, type Money, type TalkRateSource } from "@wetalk/domain";

import type { OfferChoiceView, OfferTextView, ResolvedTalkRateView } from "../../../api/slice/commercial-offer.js";
import { SubscriptionRefusalError, type SubscriptionTaxTreatmentView } from "../../../api/slice/subscription.js";
import { talkRateKindLabel } from "../../../api/slice/talk-rate.js";
import { portalLocale, portalText } from "../../../i18n/portal.js";
import { formatMoney, formatWholeMoney } from "../../../money.js";

/** Seconds in a minute — the unit conversion of a talk time figure, never a price. */
const SECOND_IN_MINUTE = 60;

function intlLocale(): string {
  return portalLocale() === "el" ? "el-GR" : "en-GB";
}

/** The page language's line of a localised pair (an offer's name or tagline). */
export function localisedOf(text: OfferTextView): string {
  return portalLocale() === "el" ? text.el : text.en;
}

/** An offer's name: its presentation's, "Pay as you go", or "Custom" for a custom offer. */
export function offerNameOf(offer: Pick<OfferChoiceView, "presentation" | "custom" | "recurrence">): string {
  if (offer.presentation !== null) return localisedOf(offer.presentation.displayName);
  if (offer.recurrence === "payg" && !offer.custom) return portalText("subscription", "card.payg_name");
  return portalText("subscription", "card.custom_name");
}

/** "120" or "120.5": a count of minutes from seconds, its fraction kept (OA-1), in the page language's digits. */
export function minuteCountText(second: number): string {
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 2 }).format(second / SECOND_IN_MINUTE);
}

/** "125 min 30 s" (or "125 min" on a whole minute): talk time as counted, per second. */
export function talkTimeText(second: number): string {
  const whole = Math.max(0, Math.trunc(second));
  const minutes = new Intl.NumberFormat(intlLocale()).format(Math.trunc(whole / SECOND_IN_MINUTE));
  const rest = whole % SECOND_IN_MINUTE;
  return rest === 0 ? portalText("subscription", "time.minutes", { minutes }) : portalText("subscription", "time.minutes_seconds", { minutes, seconds: String(rest) });
}

/** A fee, whole euros when whole ("€149", "€149.50"), as the public card writes it. */
export function feeText(amount: Money): string {
  return formatWholeMoney(amount);
}

/**
 * "Works out at {per} a minute": the fee ÷ the included minutes, half-up to a cent (design §32.12.1 `{per:o}`), in
 * integer minor units — `null` when the offer includes no seconds.
 */
export function worksOutPerMinuteOf(fee: Money, includedSecond: number): Money | null {
  if (includedSecond <= 0) return null;
  const numerator = fee.amountMinor * SECOND_IN_MINUTE;
  return money(Math.floor((2 * numerator + includedSecond) / (2 * includedSecond)), fee.currency);
}

/** The lowest rate of a set, and whether every kind costs the same (G11). */
export function lowestRateOf(rates: readonly ResolvedTalkRateView[]): { readonly lowest: Money; readonly allEqual: boolean } | null {
  const first = rates[0];
  if (first === undefined) return null;
  let lowest = first.perMinute;
  for (const one of rates) if (one.perMinute.amountMinor < lowest.amountMinor) lowest = one.perMinute;
  return { lowest, allEqual: rates.every((one) => one.perMinute.amountMinor === first.perMinute.amountMinor) };
}

/** "€0.38 per extra minute", or "from €0.22 per extra minute" while the kinds differ (G11). `null` without rates. */
export function extraRateLineOf(rates: readonly ResolvedTalkRateView[]): string | null {
  const low = lowestRateOf(rates);
  if (low === null) return null;
  return portalText("subscription", low.allEqual ? "card.extra_rate" : "card.extra_rate_from", { rate: formatMoney(low.lowest) });
}

/** "€0.38", or "from €0.22" while the kinds differ — the `{rate}` of the extra-time line (Q-8). */
export function extraRateValueOf(rates: readonly ResolvedTalkRateView[]): string | null {
  const low = lowestRateOf(rates);
  if (low === null) return null;
  return low.allEqual ? formatMoney(low.lowest) : portalText("subscription", "quote.rate_from", { rate: formatMoney(low.lowest) });
}

/** Whose price a resolved rate is (Q-9). */
export function rateSourceText(source: TalkRateSource): string {
  switch (source) {
    case "offer":
      return portalText("subscription", "quote.source.offer");
    case "account":
      return portalText("subscription", "quote.source.account");
    case "standard":
      return portalText("subscription", "quote.source.standard");
  }
}

/**
 * One line per kind: "Calls to your wetalk number: €0.38 (plan price)" — or one line for every kind ("Every call and
 * chat: €0.38 (plan price)") while every kind costs the same from the same source.
 */
export function rateLinesOf(rates: readonly ResolvedTalkRateView[]): readonly string[] {
  const first = rates[0];
  if (first !== undefined && rates.length > 1 && rates.every((one) => one.perMinute.amountMinor === first.perMinute.amountMinor && one.source === first.source)) {
    return [portalText("subscription", "quote.rate_line", { kind: portalText("core", "talk_rate.every_kind"), rate: formatMoney(first.perMinute), source: rateSourceText(first.source) })];
  }
  return rates.map((one) => portalText("subscription", "quote.rate_line", { kind: talkRateKindLabel(one.kind), rate: formatMoney(one.perMinute), source: rateSourceText(one.source) }));
}

/** "24": a VAT rate from permille, in the page language's digits ("13,5" in Greek). */
function vatPercentText(permille: number): string {
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1 }).format(permille / 10);
}

/** The `{vat_text}` of Q-5, Q-6 and Q-23: "€11.76 VAT (24%)", "€0.00 VAT (reverse charge)". */
export function vatTextOf(vat: Money, treatment: SubscriptionTaxTreatmentView | null, ratePermille: number | null): string {
  const amount = formatMoney(vat);
  if (treatment === "reverse_charge") return portalText("subscription", "vat.reverse_charge", { vat: amount });
  if (treatment === "outside_scope") return portalText("subscription", "vat.outside_scope", { vat: amount });
  if (ratePermille === null) return portalText("subscription", "vat.plain", { vat: amount });
  return portalText("subscription", "vat.rate", { vat: amount, rate: vatPercentText(ratePermille) });
}

/** "17 October 2026" in the billing timezone (the period boundaries are cut there). */
export function longDateText(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat(intlLocale(), { day: "numeric", month: "long", year: "numeric", timeZone }).format(new Date(iso));
}

/** "10:42" — when a frozen price stops holding. */
export function clockText(iso: string): string {
  return new Intl.DateTimeFormat(intlLocale(), { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

/** A list of names, joined as the page language joins them ("A, B and C"). */
export function namesText(names: readonly string[]): string {
  return new Intl.ListFormat(intlLocale(), { style: "long", type: "conjunction" }).format(names);
}

/** The refusal's code, from the API (its envelope) or from the sandbox (a domain error). */
export function refusalCodeOf(error: unknown): string | null {
  if (error instanceof ApiError) return error.code === "unknown_code" ? null : error.code;
  const code: unknown = error instanceof Error ? Reflect.get(error, "code") : undefined;
  return typeof code === "string" ? code : null;
}

/** `subscription_tax_id_required`'s reason (design §32.6.5), when the refusal kept it. */
export function taxIdReasonOf(error: unknown): string | null {
  if (error instanceof SubscriptionRefusalError) return error.reason;
  const reason: unknown = error instanceof Error ? Reflect.get(error, "reason") : undefined;
  return typeof reason === "string" ? reason : null;
}

/**
 * Design §32.12.3 Q-28: the plan screens' own sentence for a refusal they know, or `null` (the API's own message is then
 * shown, through `apiProblemText`).
 */
export function refusalTextOf(error: unknown): string | null {
  const code = refusalCodeOf(error);
  switch (code) {
    case "subscription_sale_closed":
      return portalText("subscription", "refusal.subscription_sale_closed");
    case "subscription_tax_id_required":
      return taxIdReasonOf(error) === "try_later" ? portalText("subscription", "refusal.subscription_tax_id_try_later") : portalText("subscription", "refusal.subscription_tax_id_required");
    case "subscription_consent_missing":
      return portalText("subscription", "refusal.subscription_consent_missing");
    case "offer_scope_ineligible":
      return portalText("subscription", "refusal.offer_scope_ineligible");
    case "offer_term_unsupported":
      return portalText("subscription", "refusal.offer_term_unsupported");
    case "payment_method_in_use_by_subscription":
      return portalText("subscription", "refusal.payment_method_in_use_by_subscription");
    case "subscription_renewal_unresolved":
      return portalText("subscription", "refusal.subscription_renewal_unresolved");
    case "quote_stale":
      return portalText("subscription", "refusal.quote_stale");
    case "quote_expired":
      return portalText("subscription", "refusal.quote_expired");
    case "subscription_change_unavailable":
      return portalText("subscription", "refusal.subscription_change_unavailable");
    default:
      return null;
  }
}

/** The refusals whose remedy is a new quote ("Review it again"). */
export function refusalNeedsNewQuote(error: unknown): boolean {
  const code = refusalCodeOf(error);
  return code === "quote_stale" || code === "quote_expired";
}

/** The refusals whose remedy is the organization's billing details (a tax id to add or correct). */
export function refusalNeedsBillingDetails(error: unknown): boolean {
  return refusalCodeOf(error) === "subscription_tax_id_required" && taxIdReasonOf(error) !== "try_later";
}
